/**
 * CR320: Card-Visible Search Utilities
 * 
 * Implements search that matches ONLY card-visible fields:
 * - title (derived headline)
 * - condition (qualifying spend/product)
 * - reward (bonus/discount value)
 * - merchant (promo merchant name)
 * 
 * CRITICAL: Never search terms, fine print, or evidence blobs.
 */

import type { Deal } from "@/lib/types/deals";
import type { SearchFilterConfig } from "@/lib/layout-studio/types";
import { DEFAULT_SEARCH_FILTER_CONFIG } from "@/lib/layout-studio/types";

/**
 * Build a searchable index string from card-visible fields only.
 * This is used for both filtering and suggestions.
 */
export function buildCardVisibleSearchIndex(deal: Deal): string {
  const parts: string[] = [];
  
  // 1. Title - use headline_mechanic or derive from deal data
  const title = getDealCardTitle(deal);
  if (title) parts.push(title);
  
  // 2. Condition - qualifying product/spend info
  const condition = getDealCardCondition(deal);
  if (condition) parts.push(condition);
  
  // 3. Reward - what you get
  const reward = getDealCardReward(deal);
  if (reward) parts.push(reward);
  
  // 4. Merchant - promo merchant name (where you shop)
  const merchant = getDealCardMerchant(deal);
  if (merchant) parts.push(merchant);
  
  return parts.join(" ").toLowerCase();
}

/**
 * Get the title displayed on the card (headline mechanic)
 */
export function getDealCardTitle(deal: Deal): string {
  // Use headline_mechanic if available
  if (deal.headline_mechanic) return deal.headline_mechanic;
  
  // Derive from deal type
  const spend = deal.spend_amount ?? deal.qualifying_spend_cad ?? deal.pay_amount_cad;
  const bonus = deal.bonus_amount ?? deal.bonus_value_cad ?? deal.bonus_or_discount_amount;
  
  if (spend && bonus) {
    return `Buy $${spend}, get $${bonus} bonus`;
  }
  if (bonus) {
    return `Get $${bonus} bonus`;
  }
  if (deal.discount_percent) {
    return `${deal.discount_percent}% off`;
  }
  if (deal.points_amount && deal.points_program_name) {
    return `Earn ${deal.points_amount} ${deal.points_program_name}`;
  }
  
  return "Deal available";
}

/**
 * Get the condition displayed on the card (what you need to buy/do)
 */
export function getDealCardCondition(deal: Deal): string {
  // Qualifying product name
  if (deal.qualifying_product_name) {
    return deal.qualifying_product_name;
  }
  
  // Purchase brand
  const purchaseBrand = deal.purchase_gift_card_brand ?? deal.gift_card_brand;
  if (purchaseBrand && purchaseBrand !== "Varies") {
    return purchaseBrand;
  }
  
  // Spend amount
  const spend = deal.spend_amount ?? deal.qualifying_spend_cad ?? deal.min_purchase_cad;
  if (spend) {
    return `Spend $${spend}`;
  }
  
  return "";
}

/**
 * Get the reward displayed on the card (what you get)
 */
export function getDealCardReward(deal: Deal): string {
  const parts: string[] = [];
  
  // Reward brand name
  if (deal.reward_brand_name) {
    parts.push(deal.reward_brand_name);
  }
  
  // Bonus merchant
  if (deal.bonus_merchant && deal.bonus_merchant !== deal.reward_brand_name) {
    parts.push(deal.bonus_merchant);
  }
  
  // Bonus amount
  const bonus = deal.bonus_amount ?? deal.bonus_value_cad;
  if (bonus) {
    parts.push(`$${bonus} bonus`);
  }
  
  // Discount amount/percent
  if (deal.discount_percent) {
    parts.push(`${deal.discount_percent}% off`);
  }
  if (deal.discount_amount_cad) {
    parts.push(`$${deal.discount_amount_cad} off`);
  }
  
  // Points
  if (deal.points_amount) {
    const programName = deal.points_program_name || "points";
    parts.push(`${deal.points_amount.toLocaleString()} ${programName}`);
  }
  
  return parts.join(" ");
}

/**
 * Get the merchant displayed on the card (where you shop)
 */
export function getDealCardMerchant(deal: Deal): string {
  return deal.promo_merchant_name || deal.merchant || "";
}

/**
 * Filter deals by search query using the configured scope.
 * 
 * Scope options:
 * - "card_visible": Only search title, condition, reward, merchant
 * - "title_only": Only search headline/title
 * - "title_merchant": Only search title and merchant
 */
export function filterDealsBySearch(
  deals: Deal[],
  searchQuery: string,
  config?: SearchFilterConfig
): Deal[] {
  const mergedConfig = { ...DEFAULT_SEARCH_FILTER_CONFIG, ...config };
  const trimmedQuery = searchQuery.trim().toLowerCase();
  
  if (trimmedQuery.length < mergedConfig.minChars) {
    return deals;
  }
  
  return deals.filter((deal) => {
    switch (mergedConfig.scope) {
      case "title_only": {
        const title = getDealCardTitle(deal).toLowerCase();
        return title.includes(trimmedQuery);
      }
      
      case "title_merchant": {
        const title = getDealCardTitle(deal).toLowerCase();
        const merchant = getDealCardMerchant(deal).toLowerCase();
        return title.includes(trimmedQuery) || merchant.includes(trimmedQuery);
      }
      
      case "card_visible":
      default: {
        // Use the full card-visible search index
        const searchIndex = buildCardVisibleSearchIndex(deal);
        return searchIndex.includes(trimmedQuery);
      }
    }
  });
}
